import Checkbox from 'lib/components/checkbox/checkbox'
import { ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useBillingPlans } from './billing-plans-context'
import { displayPlanPrice, displayPrice, displayTotalPrice, getTotalPrice } from 'lib/util/billing'
import { request } from 'lib/api/fetch-api'
import { useMutation } from '@tanstack/react-query'
import { Skeleton } from 'lib/components/skeleton/skeleton'
import { displayDateWithoutTime } from 'lib/util/date'
import { InvoicePreview } from 'interfaces/invoice'
import { camelCaseKeys } from 'lib/object/utils'

function getSubscriptionItems(currentSubscriptions, selectedPlans) {
  const items = []
  const planTypes = [
    { subKey: 'platformSubscription', planKey: 'platformPlan' },
    { subKey: 'creativeSubscription', planKey: 'creativePlan' },
    { subKey: 'professionalSubscription', planKey: 'professionalPlan' },
  ]
  planTypes.forEach(({ subKey, planKey }) => {
    const currentSub = currentSubscriptions[subKey]
    const selectedPlan = selectedPlans[planKey]
    if (currentSub && selectedPlan) {
      if (currentSub.picklePlan.id !== selectedPlan.id) {
        items.push({ price: selectedPlan.stripePlanId, quantity: 1 })
        items.push({ id: currentSub.stripeItemId, deleted: true })
      }
    } else if (currentSub && !selectedPlan) {
      items.push({ id: currentSub.stripeItemId, deleted: true })
    }
  })
  return items
}

export default function BillingModalReview() {
  const { company, currentSubscriptions, selectedPlans } = useBillingPlans()
  const [checked, setChecked] = useState(false)
  const [invoice, setInvoice] = useState<InvoicePreview>(null)
  const totalCurrent = getTotalPrice(
    Object.values([
      currentSubscriptions.platformSubscription,
      currentSubscriptions.creativeSubscription,
      currentSubscriptions.professionalSubscription,
    ])
      .filter(Boolean)
      .map((sub) => sub.picklePlan)
      .filter(Boolean),
  )
  const totalSelected = getTotalPrice(Object.values(selectedPlans))
  const isUpgrade = totalSelected > totalCurrent
  const hasSubscription = !!currentSubscriptions?.platformSubscription

  const createInvoicePreviewsMutation = useMutation({
    mutationFn: () => {
      return request({
        endpoint: 'createInvoicePreviews',
        body: {
          company_id: company.id,
          stripe_subscription_id: currentSubscriptions?.platformSubscription?.stripeSubscriptionId || '',
          subscription_items: getSubscriptionItems(currentSubscriptions, selectedPlans),
          subscription_proration_behavior: isUpgrade ? 'always_invoice' : 'none',
        },
      })
    },
    onSuccess: ({ data }) => {
      setInvoice(camelCaseKeys(data) as InvoicePreview)
      console.log('Invoice preview created successfully', data)
      console.log('Invoice preview created successfully', camelCaseKeys(data))
    },
    onError: (error) => {
      console.error('Error creating invoice preview', error)
    },
  })

  useEffect(() => {
    if (hasSubscription) createInvoicePreviewsMutation.mutate()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-text-peppercorn-900">
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
        <div className="tw-flex tw-w-1/2 tw-flex-col tw-items-start tw-justify-center tw-gap-2">
          <div className="tw-text-sm tw-font-semibold tw-text-peppercorn">Current</div>
          {createInvoicePreviewsMutation.isSuccess || !hasSubscription ? (
            <div className="tw-h-36 tw-w-full tw-rounded tw-border tw-border-solid tw-border-peppercorn-200 tw-p-4">
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {currentSubscriptions.platformSubscription?.picklePlan && (
                  <>
                    <div>{currentSubscriptions.platformSubscription.picklePlan.name}</div>
                    <div>{displayPlanPrice(currentSubscriptions.platformSubscription.picklePlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {currentSubscriptions.creativeSubscription?.picklePlan && (
                  <>
                    <div>
                      {currentSubscriptions.creativeSubscription.picklePlan.name} -{' '}
                      {currentSubscriptions.creativeSubscription.picklePlan.dailyTime / 60}hrs
                    </div>
                    <div>{displayPlanPrice(currentSubscriptions.creativeSubscription.picklePlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {currentSubscriptions.professionalSubscription?.picklePlan && (
                  <>
                    <div>{currentSubscriptions.professionalSubscription.picklePlan.name}</div>
                    <div>{displayPlanPrice(currentSubscriptions.professionalSubscription.picklePlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-mt-2 tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch tw-font-semibold">
                <div>Total</div>
                <div>
                  {displayTotalPrice(
                    Object.values([
                      currentSubscriptions.platformSubscription,
                      currentSubscriptions.creativeSubscription,
                      currentSubscriptions.professionalSubscription,
                    ])
                      .filter(Boolean)
                      .map((sub) => sub.picklePlan)
                      .filter(Boolean),
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Skeleton className="tw-h-36 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
          )}
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
          <ArrowRight />
        </div>
        <div className="tw-flex tw-w-1/2 tw-flex-col tw-items-start tw-justify-center tw-gap-2">
          <div className="tw-text-sm tw-font-semibold tw-text-peppercorn">New</div>
          {createInvoicePreviewsMutation.isSuccess || !hasSubscription ? (
            <div className="tw-h-36 tw-w-full tw-rounded tw-border tw-border-solid tw-border-peppercorn-200 tw-p-4">
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {selectedPlans.platformPlan && (
                  <>
                    <div>{selectedPlans.platformPlan.name}</div>
                    <div>{displayPlanPrice(selectedPlans.platformPlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {selectedPlans.creativePlan && (
                  <>
                    <div>
                      {selectedPlans.creativePlan.name} - {selectedPlans.creativePlan.dailyTime / 60}hrs
                    </div>
                    <div>{displayPlanPrice(selectedPlans.creativePlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
                {selectedPlans.professionalPlan && (
                  <>
                    <div>{selectedPlans.professionalPlan.name}</div>
                    <div>{displayPlanPrice(selectedPlans.professionalPlan)}</div>
                  </>
                )}
              </div>
              <div className="tw-mt-2 tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch tw-font-semibold">
                <div>Total</div>
                <div>{displayTotalPrice(Object.values(selectedPlans))}</div>
              </div>
            </div>
          ) : (
            <Skeleton className="tw-h-36 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
          )}
        </div>
      </div>
      <div className="tw-border-1/2 tw-border-b tw-border-solid tw-border-peppercorn-200" />
      {createInvoicePreviewsMutation.isSuccess || !hasSubscription ? (
        <div className="tw-flex tw-h-36 tw-flex-col tw-items-start tw-gap-2 tw-self-stretch tw-rounded tw-border tw-border-peppercorn-900 tw-bg-peppercorn-900 tw-p-4 tw-text-white">
          {invoice?.totalExcludingTax && (
            <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
              <div>{isUpgrade ? 'Today’s prorated subtotal' : 'Next invoice subtotal'}</div>
              <div>{displayPrice(invoice.totalExcludingTax, company.currency)}</div>
            </div>
          )}
          {invoice?.taxPercent && (
            <div className="tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch">
              <div>Sales tax ({invoice.taxPercent}%)</div>
              <div>{displayPrice(invoice.taxPercent, company.currency)}</div>
            </div>
          )}
          {invoice?.total ? (
            <div className="tw-mt-2 tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch tw-font-semibold">
              <div>{isUpgrade ? 'Today’s prorated total' : 'Next invoice total'}</div>
              <div>{displayPrice(invoice.total, company.currency)}</div>
            </div>
          ) : (
            <div className="tw-mt-2 tw-flex tw-h-5 tw-items-center tw-justify-between tw-self-stretch tw-font-semibold">
              <div>Today&#39;s total</div>
              <div>{displayTotalPrice(Object.values(selectedPlans))}</div>
            </div>
          )}
          {company.nextBillingDate && (
            <div>
              Your new plans will be billed starting next cycle on {displayDateWithoutTime(company.nextBillingDate)}
            </div>
          )}
        </div>
      ) : (
        <Skeleton className="tw-h-36 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
      )}
      <div className="tw-flex tw-items-start tw-gap-2 tw-self-stretch">
        <div className="tw-pt-1">
          <Checkbox isChecked={checked} onClick={() => setChecked(!checked)} />
        </div>
        <div>
          By clicking this box, you are authorizing us to immediately charge your payment method for the full amount for
          any subscription plan changes. Any cancellations will take place at the end of their billing period, and all
          new subscriptions / upgrades are non-refundable and are immediately paid in full. If you have any questions
          about this, please contact us.
        </div>
      </div>
    </div>
  )
}
